// organize-imports-ignore
import { i18next, initI18Next } from '@ingenico/react-i18n';
import { logError } from '@ingenico/util-common';
import { ALL_LNGS_NAMESPACES, DEFAULT_LNGS, DEFAULT_LNGS_NAMESPACE, SUPPORTED_LNGS } from '@ingenico/util-i18n-config';
import { getAuthService, initM360App } from '@ingenico/util-mfe-shell';
import type { CommonAndProjectEnvDataAndSchemas, EnvironmentData } from '@ingenico/util-mfe-shell/env';
import { CommonEnvKey } from '@ingenico/util-mfe-shell/env';
import { LicenseInfo } from '@mui/x-license';
import * as ReactDOM from 'react-dom/client';
import 'reflect-metadata';
import { finalize, firstValueFrom, shareReplay } from 'rxjs';
import { FloatingMswFallbackNotice } from './components/FloatingMswFallbackNotice';
import { FloatingStandaloneBanner } from './components/FloatingStandaloneBanner';
import { FloatingUserTypeSwitch } from './components/FloatingUserTypeSwitch';
import { StandaloneShell } from './components/standalone-shell/StandaloneShell';
import type { DeviceManagementEnvDataAndSchemas } from './config.const.ts';
import { appContainer } from './hexa/appContainer.const';
import { appContainerInit, envValuesIt, getIsStandaloneMswFallbackActive } from './hexa/hexa.util';
import { UserTypeSwitchService } from './services/userTypeSwitch.service';
import './styles.scss';
import { replaceOrgInPath, consumeReturnUrlCookie, getUrlReturnFromLocation } from './utils/browser.util';
import { deviceManagementRouter } from '../domain/utils/route.util';

UserTypeSwitchService.ensureDefaults();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
const root = ReactDOM.createRoot(rootElement);

UserTypeSwitchService.registerToggleListener(({ organizationName }) => {
  const url = new URL(window.location.href);

  const redirectToOrg = () => {
    if (organizationName) {
      window.location.replace(url.origin + replaceOrgInPath(url.pathname, organizationName));
      return;
    }

    window.location.reload();
  };

  const authService = getAuthService(appContainer);
  if (authService) {
    authService
      .logout()
      .pipe(
        finalize(() => {
          redirectToOrg();
        }),
      )
      .subscribe({
        // eslint-disable-next-line no-console
        error: (error) => console.error('Failed to logout via Keycloak service', error),
      });
    return;
  }

  redirectToOrg();
});

/**
 * If this line below is non-commented, it becames impossible to swap the application from mock and real application;
 * Even if you change the application tab on the devTools, it will reset back to non-mock version.
 */
// UserTypeSwitchService.disableApiSdkMocksInStandalone();

await Promise.all([
  initI18Next({
    supportedLanguages: SUPPORTED_LNGS,
    defaultLanguage: DEFAULT_LNGS,
    defaultNamespaces: Object.values(DEFAULT_LNGS_NAMESPACE),
    allNamespaces: Object.values(ALL_LNGS_NAMESPACES),
    translationsRoot: '/assets/locales',
  }),
  appContainerInit(),
]);

const environmentValues =
  appContainer.get<EnvironmentData<CommonAndProjectEnvDataAndSchemas<DeviceManagementEnvDataAndSchemas>>>(envValuesIt);

LicenseInfo.setLicenseKey(environmentValues[CommonEnvKey.DatagridProLicenceKey]);

const resolvedClientId = UserTypeSwitchService.resolveClientId(
  environmentValues[CommonEnvKey.AuthClientId],
  process.env.NX_PUBLIC_AUTH_CLIENT_ID_ALT,
  process.env.NX_PUBLIC_AUTH_CLIENT_ID_DEVELOPER,
);

const init$ = initM360App(appContainer, {
  authorizationServerOrigin: environmentValues[CommonEnvKey.AuthOrigin],
  apiOrigin: environmentValues[CommonEnvKey.ApiOrigin],
  appBaseHref: environmentValues[CommonEnvKey.PortalBaseHref],
  clientId: resolvedClientId,
  userType: 'organizations',
}).pipe(shareReplay(1));

init$.subscribe({
  error: logError,
});

const appInitPromise = firstValueFrom(init$).then(() => {
  const returnUrl = consumeReturnUrlCookie();
  if (returnUrl && returnUrl !== getUrlReturnFromLocation()) {
    window.history.replaceState(null, '', returnUrl);
  }
  return deviceManagementRouter();
});

root.render(
  <>
    <StandaloneShell appInitPromise={appInitPromise} />
    <FloatingStandaloneBanner />
    <FloatingUserTypeSwitch />
    <FloatingMswFallbackNotice visible={getIsStandaloneMswFallbackActive()} />
  </>,
);
